import { ArrowLeft, ArrowRight, Check, Loader2 } from "lucide-react";
import { useState, type FormEvent } from "react";
import { toast } from "sonner";

import { leadWhatsappUrl } from "@/lib/lead-whatsapp";
import { segments } from "@/lib/site";

const revenueRanges = [
  "Até R$ 30 mil/mês",
  "R$ 30 mil a R$ 100 mil/mês",
  "R$ 100 mil a R$ 400 mil/mês",
  "Acima de R$ 400 mil/mês",
  "Ainda não faturo",
];

const employeeRanges = ["Nenhum", "1 a 5", "6 a 20", "21 a 50", "Mais de 50"];

const accountantOptions = [
  "Tenho contador e quero trocar",
  "Tenho contador e quero uma segunda opinião",
  "Não tenho contador",
  "Vou abrir a empresa agora",
];

const fieldClass =
  "min-h-12 w-full rounded-md border border-input bg-background px-4 text-base text-foreground outline-none focus-visible:ring-2 focus-visible:ring-brand";

const steps = ["Sua operação", "Contexto contábil", "Contato"] as const;

function utmFromUrl(): Record<string, string> {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  const keys = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];
  return Object.fromEntries(
    keys.filter((key) => params.get(key)).map((key) => [key, params.get(key) as string]),
  );
}

export function LeadForm({ sourcePath }: { sourcePath: string }) {
  const [step, setStep] = useState(0);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [whatsappLink, setWhatsappLink] = useState("");
  const [values, setValues] = useState({
    segment: "",
    revenue_range: "",
    employees_range: "",
    company: "",
    current_accountant: "",
    message: "",
    name: "",
    email: "",
    phone: "",
    website: "",
  });

  const set = (key: keyof typeof values) => (value: string) =>
    setValues((current) => ({ ...current, [key]: value }));

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (step < steps.length - 1) {
      setStep((current) => current + 1);
      return;
    }
    setSending(true);
    try {
      // Sem banco e sem e-mail: os dados seguem pré-preenchidos para o WhatsApp da Patrimonial.
      const result: { ok: boolean; error?: string } = { ok: true };
      if (result.ok) {
         const url = leadWhatsappUrl(values);
         setWhatsappLink(url);
        setDone(true);
         window.open(url, "_blank", "noopener,noreferrer");
      } else {
        toast.error(result.error ?? "Não foi possível enviar agora.");
      }
    } catch {
      toast.error("Não foi possível enviar agora. Tente novamente em instantes.");
    } finally {
      setSending(false);
    }
  }

  if (done) {
    return (
      <div className="rounded-lg border border-border bg-surface p-8" role="status">
        <span className="inline-flex size-11 items-center justify-center rounded-full bg-brand text-brand-foreground">
          <Check className="size-5" aria-hidden="true" />
        </span>
        <h2 className="mt-5 text-2xl font-bold text-foreground">Recebemos a sua solicitação</h2>
        <p className="mt-3 max-w-prose text-base leading-relaxed text-muted-foreground">
           A equipe da Patrimonial vai analisar as informações da sua empresa e entrar em contato
           pelo telefone informado, normalmente em até um dia útil.
        </p>
         <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="mt-4 inline-block font-semibold text-brand underline underline-offset-4">
           Continuar a conversa no WhatsApp
         </a>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="rounded-lg border border-border bg-surface p-6 md:p-8">
      <ol className="flex flex-wrap gap-3 text-sm" aria-label="Etapas do formulário">
        {steps.map((label, index) => (
          <li
            key={label}
            aria-current={index === step ? "step" : undefined}
            className={`flex items-center gap-2 rounded-full px-3 py-1 ${
              index === step
                ? "bg-brand text-brand-foreground"
                : "bg-secondary text-muted-foreground"
            }`}
          >
            <span className="font-semibold">{index + 1}</span>
            {label}
          </li>
        ))}
      </ol>

      <div className="mt-8 space-y-6">
        {step === 0 ? (
          <>
            <Field label="Qual o perfil da sua empresa?" htmlFor="segment">
              <select
                id="segment"
                className={fieldClass}
                value={values.segment}
                onChange={(event) => set("segment")(event.target.value)}
                required
              >
                <option value="">Selecione uma opção</option>
                {segments.map((segment) => (
                  <option key={segment.href} value={segment.title}>
                    {segment.title}
                  </option>
                ))}
                <option value="Outro segmento">Outro segmento</option>
              </select>
            </Field>
            <Field label="Faturamento aproximado" htmlFor="revenue">
              <select
                id="revenue"
                className={fieldClass}
                value={values.revenue_range}
                onChange={(event) => set("revenue_range")(event.target.value)}
                required
              >
                <option value="">Selecione uma opção</option>
                {revenueRanges.map((range) => (
                  <option key={range} value={range}>
                    {range}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Número de funcionários" htmlFor="employees">
              <select
                id="employees"
                className={fieldClass}
                value={values.employees_range}
                onChange={(event) => set("employees_range")(event.target.value)}
                required
              >
                <option value="">Selecione uma opção</option>
                {employeeRanges.map((range) => (
                  <option key={range} value={range}>
                    {range}
                  </option>
                ))}
              </select>
            </Field>
          </>
        ) : null}

        {step === 1 ? (
          <>
            <Field label="Nome da empresa (opcional)" htmlFor="company">
              <input
                id="company"
                className={fieldClass}
                value={values.company}
                onChange={(event) => set("company")(event.target.value)}
                autoComplete="organization"
              />
            </Field>
            <Field label="Situação contábil hoje" htmlFor="accountant">
              <select
                id="accountant"
                className={fieldClass}
                value={values.current_accountant}
                onChange={(event) => set("current_accountant")(event.target.value)}
                required
              >
                <option value="">Selecione uma opção</option>
                {accountantOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="O que você gostaria que olhássemos com atenção?" htmlFor="message">
              <textarea
                id="message"
                rows={5}
                className="w-full rounded-md border border-input bg-background p-4 text-base text-foreground outline-none focus-visible:ring-2 focus-visible:ring-brand"
                value={values.message}
                onChange={(event) => set("message")(event.target.value)}
              />
            </Field>
          </>
        ) : null}

        {step === 2 ? (
          <>
            <Field label="Seu nome" htmlFor="name">
              <input
                id="name"
                className={fieldClass}
                value={values.name}
                onChange={(event) => set("name")(event.target.value)}
                autoComplete="name"
                required
              />
            </Field>
            <Field label="E-mail" htmlFor="email">
              <input
                id="email"
                type="email"
                className={fieldClass}
                value={values.email}
                onChange={(event) => set("email")(event.target.value)}
                autoComplete="email"
                required
              />
            </Field>
            <Field label="Telefone ou WhatsApp" htmlFor="phone">
              <input
                id="phone"
                type="tel"
                className={fieldClass}
                value={values.phone}
                onChange={(event) => set("phone")(event.target.value)}
                autoComplete="tel"
              />
            </Field>
            <p className="text-sm text-muted-foreground">
              Ao enviar, você concorda com o uso dos seus dados para contato comercial, conforme a
              nossa política de privacidade.
            </p>
          </>
        ) : null}

        <div className="hidden" aria-hidden="true">
          <label htmlFor="website">Não preencha este campo</label>
          <input
            id="website"
            tabIndex={-1}
            autoComplete="off"
            value={values.website}
            onChange={(event) => set("website")(event.target.value)}
          />
        </div>
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-3">
        {step > 0 ? (
          <button
            type="button"
            onClick={() => setStep((current) => current - 1)}
            className="inline-flex min-h-12 items-center gap-2 rounded-md border border-input bg-background px-5 text-base font-medium text-foreground transition-colors hover:bg-accent"
          >
            <ArrowLeft className="size-4" aria-hidden="true" />
            Voltar
          </button>
        ) : null}
        <button
          type="submit"
          disabled={sending}
          className="inline-flex min-h-12 items-center gap-2 rounded-md bg-highlight px-6 text-base font-semibold text-highlight-foreground transition-colors hover:bg-highlight/90 disabled:opacity-70"
        >
          {sending ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : null}
          {step === steps.length - 1 ? "Enviar solicitação" : "Continuar"}
          {step < steps.length - 1 ? <ArrowRight className="size-4" aria-hidden="true" /> : null}
        </button>
      </div>
    </form>
  );
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label htmlFor={htmlFor} className="mb-2 block text-sm font-semibold text-foreground">
        {label}
      </label>
      {children}
    </div>
  );
}
